import React, { useEffect, useMemo } from 'react'
import * as Sentry from '@sentry/react'
import { Context } from '~/prix'
import useAPI, { APIProvider, API } from '~/prix/react/hooks/api'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import tokenParsedToUser from '../utils/shared/tokenParsedToUser'
import { useKeycloak } from './keycloak'
import { identifyAnalyticsUser } from '~/utils/client/analytics'
import systemUserAuthEventLog from '~/packages/system/systemUserAuthEventLog/systemUserAuthEventLog.definition'

const date = new Date()
const loggedLoginEventSessionStorageKey = 'systemUserAuthEventLog.loggedLoginSession'

function SystemUserAuthEventTracker() {
  const { context } = useAPI()
  const { isAuthenticated, isLoading, tokenParsed } = useKeycloak()
  const { callAction: callSystemUserAuthEventLog } = useActionFunction(systemUserAuthEventLog, {
    initialIsLoading: false,
  })

  useEffect(() => {
    if (isAuthenticated === false) {
      sessionStorage.removeItem(loggedLoginEventSessionStorageKey)
      return
    }

    if (isLoading || !isAuthenticated || !context.token || !context.user || !tokenParsed) {
      return
    }

    const tokenPayload = tokenParsed as {
      auth_time?: number
      session_state?: string
      sid?: string
    }
    const loginSession =
      tokenPayload.session_state ?? tokenPayload.sid ?? tokenPayload.auth_time ?? 'current'
    const currentLoginSession = String(context.user.id) + ':' + String(loginSession)

    if (sessionStorage.getItem(loggedLoginEventSessionStorageKey) === currentLoginSession) {
      return
    }

    sessionStorage.setItem(loggedLoginEventSessionStorageKey, currentLoginSession)
    callSystemUserAuthEventLog({ event: 'login' }).resultPromise.catch(error => {
      console.warn('Nao foi possivel registrar evento de login.', error)
    })
  }, [
    callSystemUserAuthEventLog,
    context.token,
    context.user,
    isAuthenticated,
    isLoading,
    tokenParsed,
  ])

  return null
}

export default function ApiWithContext({
  children,
  entities,
  sources,
}: Omit<API, 'isLoading' | 'context'> & { children: React.ReactNode }) {
  const { token, tokenParsed, isLoading } = useKeycloak()

  const context: Context = useMemo(
    () => ({
      ip: '127.0.0.1',
      userAgent: navigator.userAgent,
      timezoneOffset: date.getTimezoneOffset(),
      token: token ?? null,
      user: tokenParsed && tokenParsed.sub ? tokenParsedToUser(tokenParsed) : null,
      bypassAuthorization: false,
      requestId: null,
      userLogHelper: null,
    }),
    [isLoading, token, tokenParsed],
  )

  useEffect(() => {
    if (!context.user) {
      Sentry.setUser(null)
      return
    }

    Sentry.setUser({ id: String(context.user.id) })
    Sentry.setTag('user_role', context.user.roles[0] ?? 'unknown')
    Sentry.setTag('regional_unit', context.user.stateAbbreviation ?? 'national')

    identifyAnalyticsUser({
      email: context.user.email,
      id: String(context.user.id),
      roles: context.user.roles,
      regionalUnit: context.user.stateAbbreviation ? String(context.user.stateAbbreviation) : null,
    })
  }, [context.user])

  return (
    <APIProvider
      value={{
        entities,
        sources,
        context,
        isLoading,
      }}
    >
      <SystemUserAuthEventTracker />
      {children}
    </APIProvider>
  )
}
